<template>
  <v-dialog v-model="show" width="100vw" scrollable persistent>
    <v-card
      style="max-height: 90vh; display: flex; flex-direction: column"
      class="elevation-0"
    >
      <v-toolbar color="primary" dark flat>
        <v-toolbar-title v-if="bioassaySelected">
          <v-icon left>mdi-table-eye</v-icon>
          Calibración de Bioensayo -
          {{ bioassaySelected.serial || "Sin nombre" }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text
        style="overflow-y: auto; flex: 1 1 auto"
        v-if="bioassaySelected"
      >
        <v-card class="my-6 elevation-2">
          <v-card-title class="subtitle-1 font-weight-bold">
            <v-icon left color="primary">mdi-calendar-start</v-icon>
            Inicio de Calibración
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="calibrationStartDate"
                      label="Fecha de inicio de calibración"
                      prepend-icon="mdi-calendar"
                      clearable
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                      required
                      :disabled="bioassaySelected.isCompletedBioassay"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    locale="es"
                    v-model="calibrationStartDate"
                    @input="(startDateMenu = false), handleStartDateUpdate()"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <v-card class="mb-6 elevation-2" v-if="calibrationStartDate">
          <v-card-title class="subtitle-1 font-weight-bold">
            <v-icon left color="primary">mdi-timer-sand</v-icon>
            Progreso de Calibración
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col
                cols="12"
                md="4"
                v-for="(time, index) in calibrationTimes"
                :key="index"
              >
                <v-card outlined>
                  <v-card-title class="subtitle-2 d-flex align-center">
                    <v-chip small :color="time.color" dark class="mr-2">
                      {{ time.label }}
                    </v-chip>
                    <v-spacer></v-spacer>
                    <v-switch
                      v-model="time.completed"
                      :disabled="
                        !calibrationStartDate ||
                        (index > 0 && !calibrationTimes[index - 1].completed) || bioassaySelected.isCompletedBioassay
                      "
                      color="success"
                      @change="
                        handleTimeUpdate(index, time.notes, time.completed)
                      "
                    ></v-switch>
                  </v-card-title>
                  <v-card-text>
                    <v-textarea
                      v-model="time.notes"
                      label="Notas"
                      outlined
                      dense
                      :disabled="bioassaySelected.isCompletedBioassay"
                      @input="
                        handleTimeUpdate(index, time.notes, time.completed)
                      "
                    ></v-textarea>
                    <v-spacer></v-spacer>
                    <div v-if="time.completed" class="text-caption">
                      Completado el:
                      {{ time.completionDate || "Fecha no registrada" }}
                    </div>
                    <div v-else class="text-caption">Pendiente</div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <v-card class="mb-6 elevation-2" v-if="allTimesCompleted">
          <v-card-title class="subtitle-1 font-weight-bold">
            <v-icon left color="primary">mdi-calendar-end</v-icon>
            Finalización de Calibración
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="calibrationEndDate"
                      label="Fecha de finalización de calibración"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                      :disabled="bioassaySelected.isCompletedBioassay"
                      :rules="[(v) => !!v || 'Campo requerido']"
                      required
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    locale="es"
                    v-model="calibrationEndDate"
                    @input="(endDateMenu = false), handleEndDateUpdate()"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <v-card
          class="mb-6 elevation-2"
          v-if="allTimesCompleted && calibrationEndDate && showMarkers"
        >
          <v-card-title class="subtitle-1 font-weight-bold">
            <v-icon left color="primary">mdi-chart-bell-curve</v-icon>
            Niveles de Suficiencia Funcional
          </v-card-title>
          <v-card-text>
            <v-row
              v-for="(marker, index) in bioassaySelected.molecularMarkers"
              :key="index"
            >
              <v-col cols="12">
                <v-card outlined>
                  <v-card-title class="subtitle-2">
                    <v-chip color="primary" dark>
                      <v-icon left>
                        {{
                          marker.markerType == "MO"
                            ? "mdi-bacteria"
                            : "mdi-leaf"
                        }}
                      </v-icon>
                      {{
                        marker.markerType == "MO"
                          ? marker.microorganism_meta.name
                          : marker.soilFunction.es
                      }}
                    </v-chip>
                  </v-card-title>
                  <v-card-text>
                    <v-form
                      ref="formMarkers"
                      lazy-validation
                      v-if="functionalLevels[marker._id]"
                    >
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="functionalLevels[marker._id].min"
                            label="Límite inferior (cq)"
                            type="number"
                            step="0.0001"
                            outlined
                            dense
                            :rules="[(v) => !!v || 'Campo requerido']"
                            required
                            :disabled="bioassaySelected.isCompletedBioassay"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="functionalLevels[marker._id].max"
                            label="Límite superior (cq)"
                            type="number"
                            step="0.0001"
                            outlined
                            dense
                            :rules="[(v) => !!v || 'Campo requerido']"
                            required
                            :disabled="bioassaySelected.isCompletedBioassay"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-form>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12" class="text-right">
                <v-btn
                  color="primary"
                  large
                  :loading="isFinishingCalibration"
                  :disabled="bioassaySelected.isCompletedBioassay"
                  @click="handleFinishCalibration"
                >
                  <v-icon left>mdi-check</v-icon>
                  Finalizar Calibración
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";

export default {
  props: {
    value: Boolean,
  },
  data() {
    return {
      calibrationStartDate: null,
      calibrationEndDate: null,
      startDateMenu: false,
      endDateMenu: false,
      isFinishingCalibration: false,
      calibrationTimes: [
        {
          label: "Tiempo 0",
          color: "#2196F3",
          completed: false,
          completionDate: null,
          notes: null,
        },
        {
          label: "Tiempo 1",
          color: "#4CAF50",
          completed: false,
          completionDate: null,
          notes: null,
        },
        {
          label: "Tiempo 2",
          color: "#9C27B0",
          completed: false,
          completionDate: null,
          notes: null,
        },
      ],
      functionalLevels: {},
      showMarkers: false,
    };
  },
  computed: {
    ...mapState("bioassays", ["bioassaySelected"]),
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    allTimesCompleted() {
      return this.calibrationTimes.every((time) => time.completed);
    },
  },
  async mounted() {
    if (this.bioassaySelected) {
      await this.initializeData();
    }
  },
  methods: {
    ...mapMutations("alerts", ["SET_ALERT_TYPE"]),
    ...mapActions("bioassays", [
      "updateCalibrationStartDate",
      "updateCalibrationTime",
      "updateCalibrationEndDate",
      "finishCalibration",
    ]),
    ...mapActions("riskLevel", ["getLevelByMarkerId"]),
    ...mapMutations("bioassays", ["SET_BIOASSAY"]),

    async initializeData() {
      if (!this.bioassaySelected?.molecularMarkers) return;

      if (this.bioassaySelected.calibrationStartDate) {
        this.calibrationStartDate = new Date(
          this.bioassaySelected.calibrationStartDate
        )
          .toISOString()
          .substr(0, 10);
      }
      if (this.bioassaySelected.calibrationEndDate) {
        this.calibrationEndDate = new Date(
          this.bioassaySelected.calibrationEndDate
        )
          .toISOString()
          .substr(0, 10);
      }

      this.calibrationTimes.forEach((time, index) => {
        let innerTime = this.bioassaySelected.times[index];
        if (innerTime) {
          time.notes = innerTime.calibrationNotes;
          if (innerTime.isCompletedCalibration) {
            time.completed = true;
            time.completionDate = new Date(innerTime.calibrationSuccessDate)
              .toISOString()
              .substr(0, 10);
          }
        }
      });

      this.functionalLevels = {};
      for (const marker of this.bioassaySelected.molecularMarkers) {
        if (
          this.bioassaySelected.molecularMarkersCalibration &&
          this.bioassaySelected.molecularMarkersCalibration.length > 0
        ) {
          const calibrationMarker =
            this.bioassaySelected.molecularMarkersCalibration.find(
              (element) => element._markerReference == marker._id
            );
          this.functionalLevels[calibrationMarker._markerReference] = {
            min: calibrationMarker.min,
            max: calibrationMarker.max,
            _markerReference: marker._id,
            name:
              marker.markerType == "MO"
                ? marker.microorganism_meta.name
                : marker.soilFunction.es,
          };
        } else {
          let level = await this.fetchMarkers(marker._id);
          this.functionalLevels[marker._id] = {
            min: level.min,
            max: level.max,
            _markerReference: marker._id,
            name:
              marker.markerType == "MO"
                ? marker.microorganism_meta.name
                : marker.soilFunction.es,
          };
        }
      }
      this.showMarkers = true;
    },
    async fetchMarkers(markerId) {
      try {
        const payload = {
          markerId: markerId,
        };
        const response = await this.getLevelByMarkerId(payload);
        if (response.level) {
          return {
            min: response.level.riskLevelMedium.min,
            max: response.level.riskLevelMedium.max,
          };
        } else {
          return {
            min: null,
            max: null,
          };
        }
      } catch (error) {
        console.error("Error finishing calibration:", error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Error al obtener niveles funcionales: ${error.message}`,
          timeout: 5000,
        });
      }
    },

    async handleFinishCalibration() {
      if (!this.validateForm()) return;
      this.isFinishingCalibration = true;
      try {
        const payload = {
          markerList: this.functionalLevels,
        };
        const params = {
          bioassayId: this.bioassaySelected._id,
        };
        const response = await this.finishCalibration({
          body: payload,
          params,
        });
        // this.SET_BIOASSAY(response.bioassay);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: "Calibración finalizada correctamente",
          timeout: 5000,
        });
        this.bioassaySelected.isCompletedBioassay = true;
        this.$emit("update");
      } catch (error) {
        console.error("Error finishing calibration:", error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Error al finalizar calibración: ${error.message}`,
          timeout: 5000,
        });
      } finally {
        this.isFinishingCalibration = false;
      }
    },

    validateForm() {
      for (const markerId in this.functionalLevels) {
        if (
          this.functionalLevels[markerId].min === null ||
          this.functionalLevels[markerId].max === null
        ) {
          this.SET_ALERT_TYPE({
            visible: true,
            type: "error",
            text: "Debe completar los límites para todos los biomarcadores",
            timeout: 3000,
          });
          return false;
        }

        if (
          parseFloat(this.functionalLevels[markerId].min) >=
          parseFloat(this.functionalLevels[markerId].max)
        ) {
          this.SET_ALERT_TYPE({
            visible: true,
            type: "error",
            text: "El límite inferior debe ser menor que el superior",
            timeout: 3000,
          });
          return false;
        }
      }

      return true;
    },
    async handleStartDateUpdate() {
      try {
        if (!this.calibrationStartDate) return;
        let payload = {
          calibrationStartDate: this.calibrationStartDate,
        };
        let params = {
          bioassayId: this.bioassaySelected._id,
        };
        let response = await this.updateCalibrationStartDate({
          body: payload,
          params,
        });
        this.SET_BIOASSAY(response.bioassays);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: `Información guardada`,
          timeout: 5000,
        });
        this.$emit("update");
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Error al guardar fecha: ${error.message}`,
          timeout: 5000,
        });
      }
    },
    async handleTimeUpdate(index, notes, isCompletedCalibration) {
      try {
        let payload = {
          index,
          notes,
          isCompletedCalibration,
        };
        let params = {
          bioassayId: this.bioassaySelected._id,
        };
        let response = await this.updateCalibrationTime({
          body: payload,
          params,
        });
        if (isCompletedCalibration) {
          this.calibrationTimes[index].completionDate = new Date()
            .toISOString()
            .substr(0, 10);
        }
        this.SET_BIOASSAY(response.bioassays);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: `Información guardada`,
          timeout: 5000,
        });
        this.$emit("update");
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Error al guardar fecha: ${error.message}`,
          timeout: 5000,
        });
      }
    },
    async handleEndDateUpdate() {
      try {
        if (!this.calibrationEndDate) return;
        let payload = {
          calibrationEndDate: this.calibrationEndDate,
        };
        let params = {
          bioassayId: this.bioassaySelected._id,
        };
        let response = await this.updateCalibrationEndDate({
          body: payload,
          params,
        });
        this.SET_BIOASSAY(response.bioassays);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: `Información guardada`,
          timeout: 5000,
        });
        this.$emit("update");
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Error al guardar fecha: ${error.message}`,
          timeout: 5000,
        });
      }
    },
  },
};
</script>

<style scoped>
.v-card {
  margin-bottom: 20px;
  transition: all 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
}

.v-chip {
  margin-right: 8px;
  transition: transform 0.2s;
}

.v-chip:hover {
  transform: scale(1.05);
}

.v-text-field {
  margin-top: 8px;
}

.v-btn {
  margin-top: 16px;
}

/* Animaciones */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animated-card {
  animation: fadeIn 0.5s ease-out;
}
</style>
